import {
  ActionableNotification,
  DataTable,
  DataTableSkeleton,
  Link as CarbonLink,
  Pagination,
  Table,
  TableBatchAction,
  TableBatchActions,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectAll,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import { asProblem, EmptyState, FilterButton, FilterLayout, problemMessage, type ListFilters } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ComponentType, type ReactNode } from 'react';
import './logs.scss';

const PAGE_SIZES = [50, 100, 200];

export interface LogColumn<T> {
  key: string;
  header: string;
  render(row: T): ReactNode;
}

export interface LogQuery {
  search?: string;
  page: number;
  pageSize: number;
}

/** An action on the selected rows, e.g. retrying failed messages. */
export interface LogBatchAction {
  label(count: number): string;
  icon?: ComponentType;
  run(ids: string[]): void;
}

/**
 * A read-only log list for the HO portal: newest first from the server, with search, the filter panel, paging, a row
 * opening its details, and optionally an action on selected rows.
 */
export function LogTable<T>({
  label,
  queryKey,
  load,
  rowId,
  columns,
  filters,
  filterFields,
  onOpen,
  batchAction,
  pageSizes = PAGE_SIZES,
}: {
  label: string;
  queryKey: readonly unknown[];
  /** `totalCapped`: more match than the server counts (ADR 0127); the pages then go on until one comes back short. */
  load(query: LogQuery): Promise<{ items: T[]; total: number | string; totalCapped?: boolean }>;
  rowId(row: T): string;
  columns: LogColumn<T>[];
  filters: ListFilters & { applied: Readonly<Record<string, string>> };
  filterFields: ReactNode;
  onOpen?(row: T): void;
  batchAction?: LogBatchAction;
  /** Page sizes offered; logs page by 50/100/200, other read-only lists by 25/50/100 (DESIGN_SYSTEM.md §13). */
  pageSizes?: number[];
}) {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  // Back to the first page when the filters change.
  const filterKey = JSON.stringify(filters.applied);
  const [paging, setPaging] = useState({ filterKey, page: 1 });
  const page = paging.filterKey === filterKey ? paging.page : 1;
  const setPage = (next: number) => setPaging({ filterKey, page: next });
  const [pageSize, setPageSize] = useState(pageSizes[0] ?? 50);
  const query = { search: search || undefined, page, pageSize };
  const list = useQuery({
    queryKey: [...queryKey, filters.applied, query],
    queryFn: () => load(query),
    placeholderData: keepPreviousData,
  });
  const items = list.data?.items ?? [];
  const byId = new Map(items.map((row) => [rowId(row), row]));
  const headers = columns.map((c) => ({ key: c.key, header: c.header }));

  return (
    <div className="pos-log">
      {list.error && (
        <ActionableNotification
          inline
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(list.error))}
          actionButtonLabel={t('admin.retry')}
          onActionButtonClick={() => void list.refetch()}
        />
      )}
      <FilterLayout filters={filters} fields={filterFields}>
        {list.isLoading ? (
          <DataTableSkeleton
            columnCount={columns.length + (batchAction ? 1 : 0)}
            rowCount={5}
            headers={headers}
            showToolbar
            aria-label={label}
          />
        ) : (
          <DataTable rows={items.map((row) => ({ id: rowId(row) }))} headers={headers}>
            {({
              rows,
              headers: tableHeaders,
              getTableProps,
              getHeaderProps,
              getRowProps,
              getSelectionProps,
              getToolbarProps,
              getBatchActionProps,
              selectedRows,
            }) => {
              const batch = getBatchActionProps();
              return (
                <TableContainer>
                  <TableToolbar {...getToolbarProps()}>
                    {batchAction && (
                      <TableBatchActions
                        {...batch}
                        translateWithId={(id: string, state?: { totalSelected?: number }) =>
                          id === 'carbon.table.batch.cancel'
                            ? t('common.cancel')
                            : t('logs.selected', { count: state?.totalSelected ?? selectedRows.length })
                        }
                      >
                        <TableBatchAction
                          renderIcon={batchAction.icon}
                          onClick={() => batchAction.run(selectedRows.map((r) => r.id))}
                        >
                          {batchAction.label(selectedRows.length)}
                        </TableBatchAction>
                      </TableBatchActions>
                    )}
                    <TableToolbarContent className="pos-table-toolbar__content" aria-hidden={batch.shouldShowBatchActions}>
                      <TableToolbarSearch
                        className="pos-table-toolbar__search"
                        persistent
                        labelText={t('admin.search')}
                        placeholder={t('admin.search')}
                        closeButtonLabelText={t('carbon.clearSearch')}
                        tabIndex={batch.shouldShowBatchActions ? -1 : 0}
                        onChange={(_event, value) => {
                          setSearch(value ?? '');
                          setPage(1);
                        }}
                      />
                      <FilterButton filters={filters} />
                    </TableToolbarContent>
                  </TableToolbar>
                  <Table {...getTableProps()} aria-label={label}>
                    <TableHead>
                      <TableRow>
                        {batchAction && <TableSelectAll {...getSelectionProps()} />}
                        {tableHeaders.map((header) => {
                          const { key, ...props } = getHeaderProps({ header });
                          return (
                            <TableHeader key={key} {...props}>
                              {header.header}
                            </TableHeader>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => {
                        const record = byId.get(row.id);
                        if (!record) return null;
                        const { key, ...rowProps } = getRowProps({ row });
                        return (
                          <TableRow
                            key={key}
                            {...rowProps}
                            className={onOpen ? 'pos-list-table__row--clickable' : undefined}
                            onClick={
                              onOpen
                                ? (e) => {
                                    // Ticking the row's checkbox selects it and the first cell's button opens it on its
                                    // own; anywhere else on the row opens it too, as a convenience for the mouse.
                                    if (!(e.target as HTMLElement).closest('input, label, button, a')) onOpen(record);
                                  }
                                : undefined
                            }
                          >
                            {batchAction && <TableSelectRow {...getSelectionProps({ row })} />}
                            {columns.map((column, index) => (
                              <TableCell key={column.key}>
                                {onOpen && index === 0 ? (
                                  <CarbonLink
                                    as="button"
                                    type="button"
                                    className="pos-list-table__open"
                                    onClick={() => onOpen(record)}
                                  >
                                    {column.render(record)}
                                  </CarbonLink>
                                ) : (
                                  column.render(record)
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {items.length === 0 && !list.error && (
                    <EmptyState
                      heading={t(search || filters.count > 0 ? 'admin.noResultsTitle' : 'admin.emptyTitle')}
                      description={search || filters.count > 0 ? t('admin.noResultsHint') : undefined}
                    />
                  )}
                  <Pagination
                    itemsPerPageText={t('carbon.itemsPerPage')}
                    itemRangeText={(min, max, total) => t('carbon.itemRange', { min, max, total })}
                    pageRangeText={(_current, total) => t('carbon.pageRange', { total })}
                    backwardText={t('carbon.previousPage')}
                    forwardText={t('carbon.nextPage')}
                    pageNumberText={t('carbon.pageNumber')}
                    page={page}
                    pageSize={pageSize}
                    pageSizes={pageSizes}
                    totalItems={Number(list.data?.total ?? 0)}
                    {...(list.data?.totalCapped && {
                      pagesUnknown: true,
                      isLastPage: (list.data?.items.length ?? 0) < pageSize,
                      itemText: (min: number, max: number) =>
                        t('carbon.itemRangeMany', { min, max, total: Number(list.data?.total ?? 0).toLocaleString(i18n.language) }),
                      pageText: (p: number) => t('carbon.pageOnly', { page: p }),
                    })}
                    onChange={({ page: nextPage, pageSize: nextSize }) => {
                      setPage(nextPage);
                      setPageSize(nextSize);
                    }}
                  />
                </TableContainer>
              );
            }}
          </DataTable>
        )}
      </FilterLayout>
    </div>
  );
}
